import { useState, useEffect, useCallback, useMemo } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Clock, Users, Search, Flag, ChevronLeft, Save, X } from "lucide-react";
import ScoreCsvImport from "@/components/ScoreCsvImport";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});
const DEFAULT_PAR = Array(18).fill(4);

const fmtDay = (d) => {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
  } catch { return d; }
};

export default function MarshallCapture({ tournament, canWrite }) {
  const tid = tournament.tournament_id;
  const cats = tournament.categories || [];
  const par = tournament.par_holes || DEFAULT_PAR;

  const [teetimes, setTeetimes] = useState([]);
  const [players, setPlayers] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catId, setCatId] = useState(cats[0]?.category_id || "__all__");
  const [round, setRound] = useState(1);
  const [editing, setEditing] = useState(null); // {label, sub, players:[{player_id,name,handicap_index}]}
  const [query, setQuery] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tt, pl, sc] = await Promise.all([
        fetch(`${API}/club/tournaments/${tid}/teetimes`, { credentials: "include", headers: AUTH() }),
        fetch(`${API}/club/tournaments/${tid}/players`, { credentials: "include", headers: AUTH() }),
        fetch(`${API}/club/tournaments/${tid}/scores`, { credentials: "include", headers: AUTH() }),
      ]);
      if (tt.ok) setTeetimes(await tt.json());
      if (pl.ok) setPlayers(await pl.json());
      if (sc.ok) setScores(await sc.json());
    } finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const scoreOf = useCallback((pid) => scores.find((s) => s.player_id === pid && s.round_num === round), [scores, round]);
  const maxRounds = useMemo(() => {
    if (catId !== "__all__") {
      const c = cats.find((x) => x.category_id === catId);
      return c?.rounds || tournament.rounds || 1;
    }
    return tournament.rounds || 1;
  }, [catId, cats, tournament.rounds]);

  // groups for the current category + round
  const groups = useMemo(() => {
    const docs = teetimes.filter((t) => t.round_num === round && (catId === "__all__" || t.category_id === catId));
    const out = [];
    docs.forEach((d) => d.groups.forEach((g) => out.push({ ...g, category_name: d.category_name, playing_date: d.playing_date })));
    return out.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  }, [teetimes, round, catId]);

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    return players.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.category_name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [query, players]);

  if (editing) {
    return <GroupCapture tid={tid} round={round} par={par} players={editing.players} label={editing.label} sub={editing.sub}
      tournament={tournament} canWrite={canWrite} scoreOf={scoreOf} onBack={() => setEditing(null)} onSaved={fetchAll} />;
  }

  const groupProgress = (g) => {
    let done = 0, partial = 0;
    g.players.forEach((p) => {
      const s = scoreOf(p.player_id);
      if (s?.status === "submitted") done += 1;
      else if (s) partial += 1;
    });
    return { done, partial, total: g.players.length };
  };

  return (
    <div data-testid="marshall-capture">
      {/* Day / round + category selectors */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-4">
        {cats.length > 0 && (
          <div>
            <div className="text-[11px] uppercase font-semibold text-[#94A3B8] mb-1">Categoría</div>
            <div className="flex gap-1 flex-wrap">
              {cats.map((c) => (
                <button key={c.category_id} onClick={() => setCatId(c.category_id)} data-testid={`mc-cat-${c.category_id}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${catId === c.category_id ? "bg-[#1B4D3E] text-white" : "bg-gray-100 text-[#64748B] hover:bg-gray-200"}`}>{c.name}</button>
              ))}
            </div>
          </div>
        )}
        <div>
          <div className="text-[11px] uppercase font-semibold text-[#94A3B8] mb-1">Día / Ronda</div>
          <div className="flex gap-1 flex-wrap">
            {Array.from({ length: maxRounds }, (_, i) => i + 1).map((r) => {
              const dd = teetimes.find((t) => t.round_num === r && (catId === "__all__" || t.category_id === catId))?.playing_date;
              return (
                <button key={r} onClick={() => setRound(r)} data-testid={`mc-round-${r}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${round === r ? "bg-[#1B4D3E] text-white" : "bg-gray-100 text-[#64748B] hover:bg-gray-200"}`}>
                  R{r}{dd ? ` · ${fmtDay(dd)}` : ""}
                </button>
              );
            })}
          </div>
        </div>
        </div>
        {canWrite && <ScoreCsvImport tid={tid} cats={cats} defaultRound={round} defaultCategoryId={catId !== "__all__" ? catId : undefined} onImported={fetchAll} />}
      </div>

      {/* Straggler search */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar jugador suelto por nombre o categoría..." className="pl-9" data-testid="mc-search" />
        {searchResults.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow-lg overflow-hidden" data-testid="mc-search-results">
            {searchResults.map((p) => {
              const s = scoreOf(p.player_id);
              return (
                <button key={p.player_id} onClick={() => { setEditing({ label: p.name, sub: p.category_name || "", players: [p] }); setQuery(""); }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 text-left" data-testid={`mc-search-${p.player_id}`}>
                  <span><span className="font-medium text-[#0F172A]">{p.name}</span> <span className="text-xs text-[#94A3B8]">· {p.category_name || "—"}</span></span>
                  {s?.status === "submitted" && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Flag className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-[#64748B] font-medium">No hay tee times generados para esta ronda/categoría</p>
          <p className="text-xs text-[#94A3B8]">Genera las salidas en la pestaña "Tee Times" o usa el buscador de arriba.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="mc-groups">
          {groups.map((g, gi) => {
            const prog = groupProgress(g);
            const complete = prog.done === prog.total;
            return (
              <button key={gi} onClick={() => setEditing({ label: `Grupo ${g.group_number} · ${g.time || ""}${g.hole ? ` · Hoyo ${g.hole}` : ""}`, sub: g.category_name, players: g.players })}
                className={`text-left border rounded-xl p-3 transition-colors hover:border-[#1B4D3E] ${complete ? "bg-green-50/50 border-green-200" : "bg-white"}`} data-testid={`mc-group-${gi}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-bold text-[#0F172A] flex items-center gap-1.5"><Users className="w-4 h-4 text-[#1B4D3E]" /> Grupo {g.group_number}</span>
                  <span className="text-xs font-semibold text-[#1B4D3E]">{g.time || "—"} {g.hole ? `H${g.hole}` : ""}</span>
                </div>
                <div className="space-y-0.5 mb-2">
                  {g.players.map((p) => {
                    const s = scoreOf(p.player_id);
                    return (
                      <div key={p.player_id} className="flex items-center justify-between text-xs">
                        <span className="text-[#334155] truncate">{p.name}</span>
                        {s?.status === "submitted" ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" /> : s ? <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" /> : <span className="w-3.5" />}
                      </div>
                    );
                  })}
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${complete ? "bg-green-100 text-green-700" : prog.done + prog.partial > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
                  {prog.done}/{prog.total} tarjetas{prog.partial > 0 ? ` (${prog.partial} parcial)` : ""}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GroupCapture({ tid, round, par, players, label, sub, tournament, canWrite, scoreOf, onBack, onSaved }) {
  const catName = players[0]?.category_name;
  const csnap = (tournament.categories || []).find((c) => c.name === catName);
  const isStableford = ((csnap?.scoring_format) || tournament.scoring_format) === "stableford";

  const [grid, setGrid] = useState(() => {
    const init = {};
    players.forEach((p) => {
      const s = scoreOf(p.player_id);
      init[p.player_id] = s?.holes ? s.holes.map((h) => (h == null ? "" : String(h))) : Array(18).fill("");
    });
    return init;
  });
  const [statusByPid, setStatusByPid] = useState(() => {
    const init = {};
    players.forEach((p) => { init[p.player_id] = scoreOf(p.player_id)?.result_status || ""; });
    return init;
  });
  const [saving, setSaving] = useState(false);

  const setCell = (pid, i, v) => setGrid((g) => {
    const row = [...g[pid]];
    let val = v.toUpperCase().replace(/[^0-9X]/g, "").slice(0, 2);
    if (val.includes("X")) val = isStableford ? "X" : "";
    row[i] = val;
    return { ...g, [pid]: row };
  });
  const setStatus = (pid, v) => setStatusByPid((s) => ({ ...s, [pid]: s[pid] === v ? "" : v }));

  const total = (pid) => grid[pid].reduce((a, h) => a + (parseInt(h) || 0), 0);
  const filled = (pid) => grid[pid].filter((h) => h !== "").length;

  const saveAll = async () => {
    setSaving(true);
    let ok = 0, fail = 0;
    for (const p of players) {
      const row = grid[p.player_id];
      const rs = statusByPid[p.player_id] || null;
      if (!rs && row.every((h) => h === "")) continue; // skip empty rows unless NS/DQ
      try {
        const res = await fetch(`${API}/club/tournaments/${tid}/scores`, {
          method: "PUT", credentials: "include", headers: AUTH(),
          body: JSON.stringify({ player_id: p.player_id, round_num: round, result_status: rs,
            holes: row.map((h) => (h === "" ? null : (h === "X" ? "X" : parseInt(h)))) }),
        });
        if (res.ok) ok += 1; else fail += 1;
      } catch { fail += 1; }
    }
    setSaving(false);
    if (ok > 0) toast.success(`${ok} tarjeta(s) guardada(s)`);
    if (fail > 0) toast.error(`${fail} fallaron`);
    onSaved();
    onBack();
  };

  const out = Array.from({ length: 9 }, (_, i) => i);
  const inn = Array.from({ length: 9 }, (_, i) => i + 9);

  const holeCols = (idxs) => idxs.map((i) => (
    <th key={i} className="px-0.5 py-1 text-center font-semibold text-[#64748B] min-w-[34px]">
      <div>{i + 1}</div><div className="text-[9px] text-[#1B4D3E] font-normal">{par[i]}</div>
    </th>
  ));

  const rowCells = (pid, idxs, disabled) => idxs.map((i) => (
    <td key={i} className="px-0.5 py-0.5">
      <input type="text" inputMode="numeric" value={grid[pid][i]} disabled={!canWrite || disabled}
        onChange={(e) => setCell(pid, i, e.target.value)} data-testid={`mc-cell-${pid}-${i}`}
        className="w-full text-center font-bold border border-gray-200 rounded py-1 focus:border-[#1B4D3E] focus:outline-none text-sm disabled:bg-gray-100 disabled:text-gray-300" />
    </td>
  ));

  return (
    <div data-testid="mc-group-capture">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-[#1B4D3E]" data-testid="mc-back"><ChevronLeft className="w-4 h-4" /> Volver a grupos</button>
        <div className="text-right">
          <div className="font-bold text-[#0F172A]">{label}</div>
          {sub && <div className="text-xs text-[#94A3B8]">{sub} · Ronda {round}{isStableford ? " · Stableford (usa X si se levantó)" : ""}</div>}
        </div>
      </div>

      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-3 py-2 text-left font-semibold text-[#64748B] sticky left-0 bg-gray-50 min-w-[150px]">Jugador</th>
              {holeCols(out)}
              <th className="px-1 text-center font-semibold text-[#1B4D3E] bg-gray-100 min-w-[40px]">OUT</th>
              {holeCols(inn)}
              <th className="px-1 text-center font-semibold text-[#1B4D3E] bg-gray-100 min-w-[40px]">IN</th>
              <th className="px-2 text-center font-semibold text-[#0F172A] bg-gray-100 min-w-[48px]">TOT</th>
              <th className="px-2 text-center font-semibold text-[#64748B] min-w-[120px]">Estado</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => {
              const st = statusByPid[p.player_id];
              const dis = !!st;
              const o = out.reduce((a, i) => a + (parseInt(grid[p.player_id][i]) || 0), 0);
              const n = inn.reduce((a, i) => a + (parseInt(grid[p.player_id][i]) || 0), 0);
              return (
                <tr key={p.player_id} className="border-b last:border-b-0" data-testid={`mc-row-${p.player_id}`}>
                  <td className="px-3 py-1.5 sticky left-0 bg-white">
                    <div className="font-medium text-[#0F172A] text-sm truncate max-w-[150px]">{p.name}</div>
                    <div className="text-[10px] text-[#94A3B8]">Idx {p.handicap_index ?? "—"} · {filled(p.player_id)}/18</div>
                  </td>
                  {rowCells(p.player_id, out, dis)}
                  <td className="text-center font-bold text-[#1B4D3E] bg-gray-50">{dis ? "—" : (o || "—")}</td>
                  {rowCells(p.player_id, inn, dis)}
                  <td className="text-center font-bold text-[#1B4D3E] bg-gray-50">{dis ? "—" : (n || "—")}</td>
                  <td className="text-center font-bold text-[#0F172A] bg-gray-50">{dis ? st : (total(p.player_id) || "—")}</td>
                  <td className="px-2 text-center">
                    <div className="flex gap-1 justify-center">
                      {["NS", "DQ"].map((v) => (
                        <button key={v} disabled={!canWrite} onClick={() => setStatus(p.player_id, v)} data-testid={`mc-status-${p.player_id}-${v}`}
                          className={`text-[10px] px-2 py-1 rounded font-semibold border ${st === v ? (v === "DQ" ? "bg-red-600 text-white border-red-600" : "bg-amber-500 text-white border-amber-500") : "text-[#64748B] border-gray-200 hover:bg-gray-50"}`}>{v}</button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canWrite && (
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="mc-cancel"><X className="w-4 h-4 mr-1" /> Cancelar</Button>
          <Button size="sm" onClick={saveAll} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="mc-save-group">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando..." : "Guardar grupo"}
          </Button>
        </div>
      )}
    </div>
  );
}
